import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Login from './components/Login';
import Register from './components/Register';
import RoomsDashboard from './components/RoomsDashboard';
import LivekitRoom from './components/LivekitRoom';
import Admin from './components/Admin';
import AgentVersionDetails from './components/AgentVersionDetails';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import VersionDisplay from './components/VersionDisplay';
// Import the UserSettings component
import UserSettings from './components/UserSettings';
import BellaKiosk from './components/BellaKiosk';
import './App.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is already authenticated
    const checkAuth = async () => {
      try {
        const response = await fetch('/api/auth/check', {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error('Auth check error:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuth();
  }, []);

  const handleLogout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      });
      
      setIsAuthenticated(false);
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (isLoading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <Router>
      <div className="app">
        <Navbar 
          isAuthenticated={isAuthenticated} 
          user={user} 
          onLogout={handleLogout} 
        />
        
        <div className="container">
          <Routes>
            <Route 
              path="/" 
              element={
                isAuthenticated ? (
                  <Navigate to="/dashboard" />
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />
            
            <Route 
              path="/dashboard" 
              element={
                isAuthenticated ? (
                  <RoomsDashboard user={user} />
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />
            
            <Route 
              path="/login" 
              element={
                isAuthenticated ? (
                  <Navigate to="/dashboard" />
                ) : (
                  <Login setIsAuthenticated={setIsAuthenticated} setUser={setUser} />
                )
              } 
            />
            
            <Route 
              path="/register" 
              element={
                isAuthenticated ? (
                  <Navigate to="/dashboard" />
                ) : (
                  <Register setIsAuthenticated={setIsAuthenticated} setUser={setUser} />
                )
              } 
            />
            
            <Route 
              path="/forgot-password" 
              element={
                isAuthenticated ? (
                  <Navigate to="/dashboard" />
                ) : (
                  <ForgotPassword />
                )
              } 
            />
            
            <Route 
              path="/reset-password/:token" 
              element={
                isAuthenticated ? (
                  <Navigate to="/dashboard" />
                ) : (
                  <ResetPassword />
                )
              } 
            />
            
            <Route 
              path="/room/:roomName" 
              element={
                isAuthenticated ? (
                  <LivekitRoom user={user} />
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />
            
            <Route 
              path="/room" 
              element={
                isAuthenticated ? (
                  <LivekitRoom user={user} />
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />
            
            {/* Add the Settings route */}
            <Route 
              path="/settings" 
              element={
                isAuthenticated ? (
                  <UserSettings user={user} />
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />
            
            <Route 
              path="/admin" 
              element={
                isAuthenticated && user.role === 'admin' ? (
                  <Admin user={user} />
                ) : (
                  <Navigate to="/" />
                )
              } 
            />
            
            <Route 
              path="/bella-kiosk" 
              element={
                isAuthenticated && user.role === 'admin' ? (
                  <BellaKiosk user={user} />
                ) : (
                  <Navigate to="/dashboard" />
                )
              } 
            />
            
            <Route 
              path="/admin/agents/:agentId" 
              element={
                isAuthenticated && user.role === 'admin' ? (
                  <AgentVersionDetails user={user} />
                ) : (
                  <Navigate to="/" />
                )
              } 
            />
          </Routes>
        </div>
        
        <VersionDisplay />
      </div>
    </Router>
  );
}

export default App;